// Mirror of ofetch's own retry decision (see ofetch's `onError`): GET/HEAD/
// OPTIONS retry once by default, only on these status codes; payload methods
// don't retry. We replicate it so the error toast fires once — on the final
// failing attempt — instead of once per attempt.
//
// IMPORTANT: ofetch calls `onResponseError` on every attempt, then in `onError`
// decides whether to retry, re-issuing with `retry: retries - 1`. So the FINAL
// attempt reaches `onResponseError` with `retry === 0`, which makes
// `willOfetchRetry` return false and lets the toast through. The first/only
// attempt sees `retry === undefined` (the default), so we recompute the default
// here to predict whether a retry will follow. This is verified by the unit
// tests in tests/unit/utils/ofetchRetry.test.ts — keep them in sync if ofetch's
// retry defaults or status-code list ever change.
const RETRY_STATUS_CODES = new Set([408, 409, 425, 429, 500, 502, 503, 504])
const PAYLOAD_METHODS = new Set(['PATCH', 'POST', 'PUT', 'DELETE'])

export interface OfetchRetryOptions {
  method?: string
  retry?: number | boolean
  retryStatusCodes?: number[]
}

export function willOfetchRetry(
  options: OfetchRetryOptions | undefined,
  status: number | undefined
): boolean {
  // `retry: false` disables retries entirely. Any other non-numeric value
  // (undefined, or the rarely-used `retry: true`) falls back to the per-method
  // default, matching ofetch.
  if (options?.retry === false) {
    return false
  }

  const method = String(options?.method ?? 'GET').toUpperCase()
  const defaultRetries = PAYLOAD_METHODS.has(method) ? 0 : 1
  const retries =
    typeof options?.retry === 'number' ? options.retry : defaultRetries
  if (retries <= 0) {
    return false
  }

  const code = status || 500
  const retryStatusCodes = options?.retryStatusCodes
  return Array.isArray(retryStatusCodes)
    ? retryStatusCodes.includes(code)
    : RETRY_STATUS_CODES.has(code)
}
